import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import {
  Bell,
  Briefcase,
  ClipboardList,
  CreditCard,
  GraduationCap,
  History,
  MailCheck,
  Megaphone,
  UserCircle,
} from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNotificationStore } from "@/store/notificationStore";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/partner/notifications/")({
  component: PartnerNotifications,
});

function PartnerNotifications() {
  const { notifications, markAllRead, markAsRead } = useNotificationStore();

  const iconMap: Record<string, React.ReactNode> = {
    placement: <Briefcase />,
    worklog: <ClipboardList />,
    course: <GraduationCap />,
    payment: <CreditCard />,
    account: <UserCircle />,
    system: <Megaphone />,
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alert stream"
        subtitle="Manage your inbox of placements, student approvals, and institutional system announcements."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Notifications" },
        ]}
        action={
          unreadCount > 0 ? (
            <Button onClick={markAllRead} variant="outline" size="sm">
              <MailCheck />
              Clear all unread
            </Button>
          ) : null
        }
      />

      <SectionCard
        title="Broadcast history"
        actions={<History className="size-4 text-muted-foreground" aria-hidden />}
        noPadding
      >
        {notifications.length === 0 ? (
          <EmptyState
            bare
            icon={<Bell />}
            title="No notifications yet"
            description="We'll notify you when there's an update to your slots or students."
          />
        ) : (
          <ul className="divide-y divide-border">
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => markAsRead(n.id)}
                  className={cn(
                    "flex w-full items-start gap-3 px-4 py-4 text-left transition-colors hover:bg-muted/60 focus-visible:bg-muted/60 focus-visible:outline-none md:gap-4 md:px-5",
                    !n.read && "bg-muted/30",
                  )}
                >
                  <span
                    className="flex size-9 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body [&_svg]:size-4"
                    aria-hidden
                  >
                    {iconMap[n.type] || <Bell />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="mb-0.5 flex flex-wrap items-center gap-2">
                      {!n.read && (
                        <span className="size-1.5 rounded-full bg-info" aria-label="Unread" />
                      )}
                      <span
                        className={cn(
                          "text-sm",
                          n.read ? "text-body" : "font-medium text-foreground",
                        )}
                      >
                        {n.title}
                      </span>
                      {!n.read && <Badge variant="info">Urgent</Badge>}
                    </span>
                    <span className="block max-w-3xl text-sm leading-6 text-muted-foreground">
                      {n.message}
                    </span>
                  </span>
                  <span className="flex shrink-0 flex-col items-end text-caption text-muted-foreground tabular-nums">
                    <span>
                      {new Date(n.timestamp).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                    <span>
                      {new Date(n.timestamp).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
